import React from 'react';
import { Rocket, Sparkles } from 'lucide-react';
import { calculateGalacticJourney, formatCosmicDistance } from '../utils/celestialMovement';

const GalacticOdometer = ({ birthDate }) => {
  if (!birthDate) return null;
  
  // Full date, not just the year — the year alone makes anyone born this year
  // zero years old and therefore zero km travelled.
  const journey = calculateGalacticJourney(birthDate);
  
  return (
    <div className="mt-6 p-6 bg-gradient-to-br from-purple-900/30 to-indigo-900/30 border border-purple-500/30 rounded-xl relative overflow-hidden">
      {/* Animated background effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-indigo-500/5 animate-pulse" />
      
      <div className="relative z-10">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <Rocket className="w-6 h-6 text-purple-400" />
          <h3 className="font-unbounded text-xl text-white">Your Galactic Journey</h3>
          <Sparkles className="w-5 h-5 text-purple-300 animate-pulse" />
        </div>
        
        {/* Main distance */}
        <div className="mb-4">
          <p className="text-sm text-purple-300/70 mb-2">
            Since your birth on {new Date(birthDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}:
          </p>
          <div className="text-center py-4 px-6 bg-black/30 rounded-lg border border-purple-500/20">
            <div className="text-4xl font-unbounded font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-200 to-pink-200 mb-2">
              {journey.distance_value} {journey.distance_unit}
            </div>
            <div className="text-xs text-purple-300/60 font-mono">
              ({journey.light_time})
            </div>
          </div>
        </div>
        
        {/* Description */}
        <p className="text-sm text-purple-200/90 leading-relaxed mb-4">
          You have traveled through the Milky Way galaxy as a passenger on Spaceship Earth. 
          <span className="text-purple-300 font-medium"> You are currently {journey.distance_value} {journey.distance_unit} away</span> from
          the physical point in space where you were born.
        </p>
        
        {/* Stats grid */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="p-3 bg-black/20 rounded-lg border border-purple-500/10">
            <div className="text-xs text-purple-400/70 mb-1">Cosmic Speed</div>
            <div className="text-lg font-bold text-white">{journey.velocity_kms} km/s</div>
            <div className="text-xs text-purple-300/50">({journey.velocity_mph} mph)</div>
          </div>
          
          <div className="p-3 bg-black/20 rounded-lg border border-purple-500/10">
            <div className="text-xs text-purple-400/70 mb-1">Your Age</div>
            <div className="text-lg font-bold text-white">{journey.age} {journey.age_unit}</div>
            <div className="text-xs text-purple-300/50">Around Galactic Center</div>
          </div>
        </div>
        
        {/* Fun comparisons */}
        <div className="p-3 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-lg border border-purple-400/20">
          <div className="text-xs text-purple-300/70 mb-2">Cosmic Scale:</div>
          <div className="space-y-1 text-xs text-purple-200/80">
            <div>• <span className="font-mono text-purple-300">{journey.earth_circumferences}</span> trips around Earth</div>
            <div>• <span className="font-mono text-purple-300">{journey.neptune_orbits}</span> times Neptune's orbital distance</div>
            <div>• Orbiting a <span className="text-purple-300 font-medium">Supermassive Black Hole</span> at the galaxy's heart</div>
          </div>
        </div>
        
        {/* Info note */}
        <div className="mt-4 text-xs text-purple-300/60 italic">
          💡 Our Solar System races at 230 km/s (514,000 mph) around the Milky Way's center, 
          completing one full orbit every 225-250 million years.
        </div>
      </div>
    </div>
  );
};

export default GalacticOdometer;
